import type { ReactNode, RefObject } from 'react';
import type {
  TFixedPosition,
  TTourPosition,
  TTourSize,
  TTourVariant,
} from '@kognitos/lattice';

/**
 * A chapter groups related tours into a labeled section of the onboarding flow.
 * Step counts shown on tour cards are scoped to the chapter, not the overall flow.
 */
export interface IOnboardingChapter {
  /** i18n key for the chapter title shown on tour cards. */
  titleKey: string;
  /** Ordered tour IDs within this chapter. */
  tours: readonly ETourId[];
  /** Whether this chapter is conditional (e.g. only shown if exceptions occur). */
  conditional?: boolean;
}

/**
 * Chapter context returned by useOnboardingFlow, providing everything needed
 * to display chapter title and chapter-scoped step counts on tour cards.
 */
export interface IChapterContext {
  /** The chapter definition. */
  chapter: IOnboardingChapter;
  /** Total number of steps across all tours in this chapter. */
  totalSteps: number;
}

/**
 * Unique identifier for each tour in the application.
 * Add new tour IDs here as new tours are created.
 */
export enum ETourId {
  DRAFT_WELCOME = 'draft_welcome',
  DRAFT_EDITOR = 'draft_editor',
  DRAFT_AUTOMATION_EDIT_VIEW = 'draft_automation_edit_view',
  INPUTS_TRIGGERS = 'inputs_triggers',
  EXCEPTION_HIGHLIGHT = 'exception_highlight',
  SUCCESSFUL_DRAFT_RUN = 'successful_draft_run',
  VIEWING_PUBLISHED_AUTOMATION = 'viewing_published_automation',
  MONITOR_INTRO = 'monitor_intro',
  AUTOMATION_LIST_DASHBOARD = 'automation_list_dashboard',
  GUIDANCE_CENTER = 'guidance_center',
  GUIDANCE_CENTER_ACTION_ITEM = 'guidance_center_action_item',
  GUIDANCE_CENTER_ACTION_ITEM_REVIEW = 'guidance_center_action_item_review',
  INTEGRATIONS_LIST = 'integrations_list',
  PERFORMANCE_DASHBOARD = 'performance_dashboard',
  WORKSPACE_SETTINGS = 'workspace_settings',
}

/**
 * Analytics configuration for a tour.
 * Controls what gets tracked and how events are named.
 */
export interface ITourAnalyticsConfig {
  /** Whether analytics tracking is enabled for this tour. Defaults to true. */
  enabled?: boolean;
  /**
   * Prefix for all analytics events emitted by this tour.
   * Events will be named `{eventPrefix}_started`, `{eventPrefix}_completed`, etc.
   * Defaults to `tour_{tourId}`.
   */
  eventPrefix?: string;
  /** Additional metadata to include with every analytics event for this tour. */
  metadata?: Record<string, string>;
}

/**
 * Persistence configuration for remembering tour completion state.
 */
export interface ITourPersistenceConfig {
  /** Storage strategy. Defaults to 'localStorage'. */
  strategy?: 'localStorage' | 'none';
  /** Custom localStorage key. Defaults to `tour_completed_{tourId}`. */
  key?: string;
}

/**
 * Tour step configuration using i18n keys for all user-facing text.
 * Keys are resolved at render time by ManagedTourProvider.
 */
export interface ITourStepConfig {
  /** Unique identifier for this step, used in analytics events. Defaults to index. */
  id?: string;
  /**
   * DOM element to highlight. Accepts any CSS selector.
   *
   * Prefer `data-tour-id` attribute selectors over plain IDs so tour
   * anchors don't collide with semantic HTML `id` attributes.
   *
   * @example '[data-tour-id="my-element"]'                              // data-tour-id selector (preferred)
   * @example '[data-tour-id="my-element"] [data-slot="header"]'         // data-slot inside a data-tour-id
   * @example '.paragraph-highlight-exception'                           // CSS class selector
   */
  target?: string;
  /**
   * Viewport anchor for targetless steps (no spotlight).
   * Only used when `target` is omitted.
   * @default 'bottom-right'
   */
  fixedPosition?: TFixedPosition;
  /** i18n translation key for the step title. */
  titleKey: string;
  /** i18n translation key for the step description. */
  descriptionKey?: string;
  /** Preferred position of the tour card relative to the target. */
  position?: TTourPosition;
  /** Optional image to display above the content. */
  image?: ReactNode;
  /** Optional icon to display before the title. */
  icon?: ReactNode;
  /** i18n translation key for the previous button label. Falls back to lattice default. */
  previousLabelKey?: string;
  /** i18n translation key for the next button label. Falls back to lattice default. */
  nextLabelKey?: string;
  /** i18n translation key for the finish button label (shown on the last step). Falls back to lattice default. */
  finishLabelKey?: string;
}

/**
 * Complete configuration for a single tour.
 * Defines steps, appearance, analytics, and persistence behavior.
 * All user-facing text uses i18n keys resolved at render time.
 */
export interface ITourConfig {
  /** Unique tour identifier from ETourId. */
  id: ETourId;
  /** Whether the tour is enabled. Set to false to disable without removing. Defaults to true. */
  enabled?: boolean;
  /** Ordered list of steps in the tour. */
  steps: ITourStepConfig[];
  /** i18n namespace for resolving step title/description keys. Defaults to 'tours'. */
  namespace?: string;
  /** Visual variant of the tour card. Defaults to 'default'. */
  variant?: TTourVariant;
  /** Size of the tour card. Defaults to 'sm'. */
  size?: TTourSize;
  /** Analytics configuration. Enabled by default with sensible defaults. */
  analytics?: ITourAnalyticsConfig;
  /** Persistence configuration for tracking completion. */
  persistence?: ITourPersistenceConfig;
  /**
   * When true, the previous button is visible on every step including the
   * first. On step 0 the previous button acts as "Skip" (triggers onSkip).
   */
  alwaysShowPrevious?: boolean;
  /** Padding around the highlighted element in pixels. */
  spotlightPadding?: number;
  /** Border radius of the spotlight in pixels. */
  spotlightRadius?: number;
  /**
   * Optional Statsig feature gate that controls whether this tour is shown.
   * If set and the gate evaluates to false, the tour will not start.
   */
  featureGate?: string;
  /**
   * Effective step count for chapter step-count display, overriding
   * `steps.length`.
   */
  chapterStepCount?: number;
}

/**
 * Metadata for a tour analytics event.
 */
export interface ITourEventMetadata {
  tourId: string;
  stepIndex?: string;
  stepId?: string;
  stepTitle?: string;
  totalSteps?: string;
  [key: string]: string | undefined;
}

/**
 * The return type of useManagedTour hook.
 */
export interface IUseManagedTourReturn {
  /** Start the tour. No-op if already completed and persistence is enabled. */
  startTour: () => void;
  /** End the tour without marking it as completed. */
  endTour: () => void;
  /** Whether the tour is currently active. */
  isActive: boolean;
  /** Current step index (0-based). */
  currentStep: number;
  /** Total number of steps. */
  totalSteps: number;
  /** Whether the tour has been completed previously. */
  isCompleted: boolean;
  /** Reset the tour completion state, allowing it to be shown again. */
  resetTour: () => void;
}

/**
 * Props for the ManagedTourProvider component.
 */
export interface IManagedTourProviderProps {
  children: ReactNode;
  /** Tour configuration from the registry. */
  tourConfig: ITourConfig;
  /**
   * Optional subset of step IDs to show from the tour config.
   * Useful for multi-page tours where each page shows different steps.
   * If not provided, all steps are shown.
   */
  stepIds?: string[];
  /**
   * Optional conditions per step: include a step only when its value is true.
   * Steps not listed are always included. Use to hide steps when their target
   * is not visible (e.g. { "control-future-runs": hasInputs }).
   */
  stepConditions?: Record<string, boolean>;
  /**
   * Whether to persist completion state when the tour finishes.
   * Defaults to true. Set to false for intermediate phases of multi-page tours
   * where you want the next phase to still show.
   */
  persistOnComplete?: boolean;
  /**
   * Container element for targetless (fixed-position) steps.
   * The card is portaled into this element with `absolute` positioning
   * instead of viewport `fixed`. The container must have `position: relative`.
   */
  container?: RefObject<HTMLElement | null>;
  /**
   * Whether to render the dimmed overlay and spotlight.
   * Set to false for sticky nudge-style tours with targetless steps.
   * @default true
   */
  showOverlay?: boolean;
  /** Override the default onComplete behavior. */
  onComplete?: () => void;
  /** Override the default onSkip behavior. */
  onSkip?: () => void;
}
